<script setup>
import { ref, computed } from 'vue';
import { OnClickOutside } from '@vueuse/components';

// 报表筛选下拉多选: 触发按钮(标签+已选摘要+箭头) → 浮层(搜索+勾选列表+清空)。
// options: [{ value, label, count? }]; v-model 为已选 value 数组(空 = 全部)。
const props = defineProps({
  label: { type: String, default: '' },
  options: { type: Array, default: () => [] },
  modelValue: { type: Array, default: () => [] },
  placeholder: { type: String, default: '全部' },
  searchPlaceholder: { type: String, default: '搜索…' },
});
const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const query = ref('');
// 选项多时(>8)才显示搜索框
const showSearch = computed(() => props.options.length > 8);
const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return props.options;
  return props.options.filter(o => (o.label || '').toLowerCase().includes(q));
});
// 触发按钮摘要: 未选=「全部」, 已选=首项+余量; 选项被筛空时退化成计数
const summary = computed(() => {
  if (!props.modelValue.length) return props.placeholder;
  const first = props.options.find(o => o.value === props.modelValue[0]);
  const head = first ? first.label : props.modelValue[0];
  return props.modelValue.length === 1 ? head : `${head} +${props.modelValue.length - 1}`;
});

const toggle = value => {
  const next = props.modelValue.includes(value)
    ? props.modelValue.filter(v => v !== value)
    : [...props.modelValue, value];
  emit('update:modelValue', next);
};
const clear = () => emit('update:modelValue', []);
const close = () => {
  open.value = false;
  query.value = '';
};
</script>

<template>
  <OnClickOutside @trigger="close">
    <div class="relative" @keyup.esc="close">
      <button
        type="button"
        class="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border text-sm transition-colors max-w-56"
        :class="
          modelValue.length
            ? 'border-n-brand text-n-slate-12 bg-n-brand/5'
            : 'border-n-slate-6 text-n-slate-11 hover:border-n-brand bg-n-solid-2'
        "
        :aria-expanded="open"
        @click="open = !open"
      >
        <span class="text-n-slate-9 shrink-0">{{ label }}</span>
        <span class="truncate font-medium" :title="summary">{{ summary }}</span>
        <span
          class="i-lucide-chevron-down size-3.5 shrink-0 transition-transform"
          :class="open ? 'rotate-180' : ''"
        />
      </button>
      <div
        v-if="open"
        class="absolute top-[2.5rem] left-0 z-[9999] w-60 border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] shadow-lg border-n-strong dark:border-n-strong p-2"
      >
        <div v-if="showSearch" class="relative mb-1.5">
          <span class="i-lucide-search absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-9" />
          <input
            v-model="query"
            type="text"
            class="w-full h-8 pl-7 pr-2 text-sm rounded-md border border-n-slate-6 bg-n-solid-2 text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="searchPlaceholder"
          >
        </div>
        <div class="max-h-60 overflow-y-auto">
          <button
            v-for="opt in filtered"
            :key="opt.value"
            type="button"
            class="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-n-slate-4/50 text-left"
            :class="modelValue.includes(opt.value) ? 'text-n-slate-12' : 'text-n-slate-11'"
            :title="opt.label"
            @click="toggle(opt.value)"
          >
            <span
              class="size-4 rounded border flex-shrink-0 inline-flex items-center justify-center"
              :class="
                modelValue.includes(opt.value)
                  ? 'bg-n-brand border-n-brand'
                  : 'border-n-slate-7'
              "
            >
              <span
                v-if="modelValue.includes(opt.value)"
                class="i-lucide-check size-3 text-white"
              />
            </span>
            <span class="truncate flex-1">{{ opt.label }}</span>
            <span
              v-if="opt.count !== undefined"
              class="text-xs text-n-slate-9 tabular-nums shrink-0"
            >{{ opt.count }}</span>
          </button>
          <div
            v-if="!filtered.length"
            class="py-4 text-center text-xs text-n-slate-9"
          >
            无匹配项
          </div>
        </div>
        <div
          v-if="modelValue.length"
          class="pt-1.5 mt-1 border-t border-n-slate-5 flex justify-end"
        >
          <button
            type="button"
            class="text-xs text-n-brand hover:underline"
            @click="clear"
          >
            清空({{ modelValue.length }})
          </button>
        </div>
      </div>
    </div>
  </OnClickOutside>
</template>
